import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import 'react-tooltip/dist/react-tooltip.css'
import { useDispatch, useSelector } from 'react-redux'
import { selectCurrentProject } from '../redux/application-slice'
import {
  selectCurrentPage,
  selectCursorTool,
  selectCustomLabelOpen,
  selectIconMenuOpen,
  selectTextSelected,
  setCurrentPage,
  setTextSelected,
  setTotalPages,
  setZoomLevel,
} from '../redux/secondary-viewer-slice'
import debounce from 'lodash/debounce'
import NavigationHistoryPane from './viewer/navigation-history-pane'
import { POSTHOG } from '../utils/posthog-constants'
import { usePostHog } from 'posthog-js/react'
import { ProjectDocument } from '../shared/interfaces/project/document/document.interface'
import { Revision } from '../shared/interfaces/project/document/revision/revision.interface'
import { useHotkeys } from 'react-hotkeys-hook'
import { DocumentChange } from '../shared/interfaces/project/document/changes/document-change.interface'
import { DocumentViewerCommentContext } from '../contexts/document-viewer-comment-instance-context'
import SecondaryPageCount from './secondary-page-count'
import PageCountSecondary from './page-count-secondary'
import useWindowDimensions from '../hooks/use-window-dimensions'
import SecondaryChangePageOverlaySimpleController from './secondary-change-page-overlay-simple-controller'

interface DocumentViewerProps {
  selectedDocument: ProjectDocument | null
  revisionSelected?: Revision | null
  documentChangeSelected?: DocumentChange | null
  overlay?: boolean
  originalRevision?: Revision | null
  tabWidth?: number
}

const SecondaryDocumentChangeViewerSimple: React.FC<DocumentViewerProps> = ({
  selectedDocument,
  revisionSelected,
  documentChangeSelected,
  overlay = true,
  originalRevision,
  tabWidth,
}) => {
  const { width } = useWindowDimensions()
  const [renderedPages, setRenderedPages] = useState([])

  const cursorTool = useSelector(selectCursorTool)

  const scrollView = useRef<HTMLDivElement | null>(null)
  const viewer = useRef<HTMLDivElement | null>(null)
  const renderedPagesRef = useRef<number[]>(renderedPages)

  const dispatch = useDispatch()
  const posthog = usePostHog()
  const currentProject = useSelector(selectCurrentProject)
  const customLabelOpen = useSelector(selectCustomLabelOpen)
  const iconMenuOpen = useSelector(selectIconMenuOpen)
  const customLabelRef = useRef({})
  const iconMenuOpenRef = useRef({})
  const textSelected = useSelector(selectTextSelected)
  const currentPage = useSelector(selectCurrentPage)

  const documentViewerContext = useContext(DocumentViewerCommentContext)
  const { documentViewer } = documentViewerContext

  const [searchParams] = useSearchParams()

  useEffect(() => {
    const newLevel = documentViewer?.getZoomLevel() ?? 0
    documentViewer?.zoomTo(newLevel)
    dispatch(setZoomLevel(newLevel))
  }, [dispatch, documentViewer, width])

  useEffect(() => {
    if (documentChangeSelected) {
      documentViewer?.setCurrentPage(documentChangeSelected.source.page, false)
    }
  }, [documentChangeSelected, documentViewer])

  useEffect(() => {
    customLabelRef.current = customLabelOpen
  }, [customLabelOpen])

  useEffect(() => {
    //very small timeout to support order of operations between click callback and risk menu flag being updated
    setTimeout(() => {
      iconMenuOpenRef.current = iconMenuOpen
    }, 50)
  }, [iconMenuOpen])

  const setRenderedPagesRef = (data) => {
    renderedPagesRef.current = data
    setRenderedPages(data)
  }

  useHotkeys('ctrl+c', async () => {
    if (!textSelected) {
      return
    }
    await navigator.clipboard.writeText(textSelected.text ?? '')
  })

  useHotkeys('meta+c', async () => {
    if (!textSelected) {
      return
    }
    await navigator.clipboard.writeText(textSelected.text ?? '')
  })

  useEffect(() => {
    const pageCompleteCallback = (pageNumber: number) => {
      if (!renderedPagesRef.current.includes(pageNumber)) {
        setRenderedPagesRef([...renderedPagesRef.current, pageNumber])
      }
    }
    const textSelectedCallback = debounce((quads, text, pageNumber) => {
      if (customLabelRef.current) {
        return
      }
      if (quads && quads.length > 0) {
        const textSelected = documentViewer?.getSelectedText()
        const quadsSelected = documentViewer?.getSelectedTextQuads()
        dispatch(
          setTextSelected({
            quads: quadsSelected,
            text: textSelected,
            pageNumber,
          })
        )
        posthog?.capture(POSTHOG.text_selected, {
          document_uuid: selectedDocument?.uuid,
        })
      } else {
        dispatch(setTextSelected(null))
      }
    }, 50)
    const pageNumberUpdatedCallback = (pageNumber) => {
      dispatch(setCurrentPage(pageNumber))
      debounce(() => {
        posthog?.capture(POSTHOG.page_changed, {
          page: pageNumber,
          document_uuid: selectedDocument?.uuid,
          project_uuid: currentProject?.uuid,
        })
      }, 1000)()
    }
    const documentLoadedCallback = () => {
      if (!documentViewer) {
        return
      }
      const fitMode = 'FitMode'
      documentViewer?.setFitMode(documentViewer[fitMode].FitWidth)
      setRenderedPagesRef([])
    }

    if (documentViewer) {
      // Attach viewer elements
      if (!scrollView.current || !viewer.current) {
        return
      }
      documentViewer?.setScrollViewElement(scrollView.current)
      documentViewer?.setViewerElement(viewer.current)

      dispatch(setZoomLevel(documentViewer?.getZoomLevel()))
      documentViewer?.enableAnnotations()

      // Set default tool
      documentViewer?.setToolMode(documentViewer?.getTool('TextSelect'))

      // Hook up event listeners
      documentViewer?.addEventListener('pageComplete', pageCompleteCallback)
      documentViewer?.addEventListener('textSelected', textSelectedCallback)
      documentViewer?.addEventListener(
        'pageNumberUpdated',
        pageNumberUpdatedCallback
      )
      documentViewer?.addEventListener('documentLoaded', documentLoadedCallback)
    }
    return () => {
      if (documentViewer) {
        documentViewer?.removeEventListener(
          'textSelected',
          textSelectedCallback
        )
        documentViewer?.removeEventListener(
          'pageNumberUpdated',
          pageNumberUpdatedCallback
        )
        documentViewer?.removeEventListener(
          'pageComplete',
          pageCompleteCallback
        )
        documentViewer?.removeEventListener(
          'documentLoaded',
          documentLoadedCallback
        )
      }
    }
    // eslint-disable-next-line
  }, [documentViewer, setZoomLevel])

  useEffect(() => {
    if (selectedDocument && documentViewer) {
      const fileUrl = selectedDocument.optimized_file ?? selectedDocument.file
      documentViewer
        .loadDocument(fileUrl, {
          licenseKey: atob(
            'UHJvdmlzaW9uIFNvZnR3YXJlIENvcnBvcmF0aW9uIChnZXRwcm92aXNpb24uY28pOk9FTTpQcm92aXNpb246OkIrOkFNUygyMDIzMDcxNyk6RkFBNTQ5MkQwNDg3ODgwQUYzNjBCMTNBQzlBMjczNzg2MDYxMkY4NUY3MTgxQUEwREQwNDdCOEFBRDRDNkU5MDNBOTQzMUY1Qzc='
          ),
        })
        .then(() => {
          const page = searchParams.get('page')
          if (page) {
            documentViewer?.setCurrentPage(parseInt(page), false)
          }
          dispatch(setZoomLevel(documentViewer?.getZoomLevel()))
          try {
            dispatch(setTotalPages(documentViewer?.getPageCount()))
          } catch {
            //
          }
        })
    }
    // eslint-disable-next-line
  }, [selectedDocument?.id, documentViewer])

  useEffect(() => {
    const page = searchParams.get('page')
    documentViewer?.setCurrentPage(parseInt(page ?? '1'), false)
  }, [searchParams, documentViewer])

  useEffect(() => {
    if (selectedDocument?.uuid) {
      posthog?.capture(POSTHOG.document_opened, {
        document_uuid: selectedDocument?.uuid,
      })
    }
    //   eslint-disable-next-line
  }, [selectedDocument?.uuid])

  useEffect(() => {
    if (cursorTool && documentViewer) {
      documentViewer?.setToolMode(documentViewer?.getTool(cursorTool))
    }
  }, [cursorTool, documentViewer])

  const onZoomIncrease = useCallback(() => {
    const newLevel = Math.min((documentViewer?.getZoomLevel() ?? 0) + 0.15, 2)
    documentViewer?.zoomTo(newLevel)
    dispatch(setZoomLevel(newLevel))
  }, [dispatch, documentViewer])

  const onZoomDecrease = useCallback(() => {
    const newLevel = Math.max((documentViewer?.getZoomLevel() ?? 0) - 0.15, 0.3)
    documentViewer?.zoomTo(newLevel)
    dispatch(setZoomLevel(newLevel))
  }, [dispatch, documentViewer])

  const documentViewerHeader = useMemo(() => {
    return (
      <div className={'flex w-full items-center justify-center space-x-2'}>
        <div>
          <NavigationHistoryPane />
        </div>
        {revisionSelected ? (
          <PageCountSecondary
            revision={revisionSelected}
            originalRevisionPage={originalRevision?.page ?? 1}
            totalPages={selectedDocument?.total_pages ?? 0}
          />
        ) : null}
      </div>
    )
  }, [originalRevision?.page, revisionSelected, selectedDocument])

  const getResultsAbove = useMemo(() => {
    if (!currentPage) {
      return 0
    }
    const segments = revisionSelected?.segments ?? []
    return segments.filter((segment) => segment.page < currentPage).length
  }, [currentPage, revisionSelected?.segments])

  const getResultsBelow = useMemo(() => {
    if (!currentPage) {
      return 0
    }
    const segments = revisionSelected?.segments ?? []
    return segments.filter((segment) => segment.page > currentPage).length
  }, [currentPage, revisionSelected?.segments])

  const onClickGoToAboveResults = useCallback(() => {
    const nextPageAbove = revisionSelected?.segments
      ?.filter((segment) => segment.page < currentPage)
      .sort((a, b) => b.page - a.page)?.[0]?.page
    if (nextPageAbove) {
      documentViewer?.setCurrentPage(nextPageAbove, true)
    }
  }, [currentPage, documentViewer, revisionSelected?.segments])

  const onClickGotoBelowResults = useCallback(() => {
    const nextPageBelow = revisionSelected?.segments
      ?.filter((segment) => segment.page > currentPage)
      .sort((a, b) => a.page - b.page)?.[0]?.page
    if (nextPageBelow) {
      documentViewer?.setCurrentPage(nextPageBelow, true)
    }
  }, [currentPage, documentViewer, revisionSelected?.segments])

  return (
    <div
      style={{
        width: tabWidth,
      }}
      className="flex h-full max-w-full items-stretch overflow-hidden"
    >
      <div className={'flex min-w-0 flex-1 flex-grow flex-col bg-gray-100'}>
        {overlay ? (
          <div
            className={
              'flex h-10 flex-shrink-0 items-center justify-center border-b border-gray-300  bg-gray-50 px-2'
            }
          >
            <div className={'flex items-center space-x-2'}>
              <button
                className={'rounded bg-gray-100 p-1.5 shadow-sm hover:bg-white'}
                onClick={onZoomIncrease}
              >
                <PlusIcon width={20} />
              </button>
              <button
                className={'rounded bg-gray-100 p-1.5 shadow-sm hover:bg-white'}
                onClick={onZoomDecrease}
              >
                <MinusIcon width={20} />
              </button>
              <SecondaryPageCount />
            </div>
          </div>
        ) : (
          documentViewerHeader
        )}
        <div
          id="document-viewer-secondary"
          className={'flex-1 overflow-auto bg-gray-200'}
          ref={scrollView}
        >
          <div
            className="webviewer z-10 mx-auto"
            id="webviewer-wrapper-second"
            ref={viewer}
          />
        </div>
      </div>
      {selectedDocument ? (
        <SecondaryChangePageOverlaySimpleController
          pages={renderedPages}
          revisionSelected={overlay ? revisionSelected : null}
          documentChangeSelected={documentChangeSelected}
        />
      ) : null}
    </div>
  )
}

export default SecondaryDocumentChangeViewerSimple
